import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useBlocker } from 'react-router-dom'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ActionDialog } from '@e3dc-react/shell/Elements/Dialog'
import { ActionPositions, UpToThreeActions } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'

interface Props<T> {
  onSubmit: (values?: T) => void | Promise<unknown>
  explanationText: string
  isEnabled?: boolean
  hasSubmitOption?: boolean
  disableSubmit?: boolean
  /** Clean up all forms (reset values and dirty state) */
  cleanUpForms?: () => void
}

/**
 * Dialog to warn the user if they leave form(s) with unsaved changes.
 * User can leave with(out) saving or stay on the form.
 */
const ExitUnsavedFormDialog = <T,>({
  onSubmit,
  explanationText,
  isEnabled = false,
  hasSubmitOption = false,
  disableSubmit = false,
  cleanUpForms,
}: Props<T>): React.ReactElement => {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => isEnabled && currentLocation.pathname !== nextLocation.pathname)
  const { t } = useCustomTranslation()
  const [open, setOpen] = useState(isEnabled)

  useEffect(() => {
    setOpen(isEnabled)
  }, [isEnabled])

  const handleSaveChangesAndLeavePage = async (): Promise<void> => {
    await onSubmit()
    handleLeavePage()
  }

  const handleCloseDialog = (): void => {
    blocker.reset?.()
  }

  const handleLeavePage = (): void => {
    cleanUpForms?.()
    blocker.proceed?.()
    setOpen(false)
  }

  const actions: UpToThreeActions = [
    {
      name: t('common.dialogs.unsavedChanges.controls.cancel'),
      onClick: handleCloseDialog,
      style: hasSubmitOption ? 'secondary' : 'primary',
      position: hasSubmitOption ? ActionPositions.One : ActionPositions.Four,
    },
    {
      name: t('common.dialogs.unsavedChanges.controls.continue'),
      onClick: handleLeavePage,
      style: 'secondary',
      position: ActionPositions.Three,
    },
  ]

  useEffect(() => {
    if (hasSubmitOption && actions.length < 3) {
      actions.push({
        name: t('common.dialogs.unsavedChanges.controls.save'),
        onClick: handleSaveChangesAndLeavePage,
        style: 'primary',
        position: ActionPositions.Four,
        disabled: disableSubmit,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitOption, actions, disableSubmit])

  if (blocker.state === 'blocked' && open)
    return (
      <ActionDialog title={t('common.dialogs.unsavedChanges.title')} open={true} actions={actions} maxWidth="sm" onClose={handleCloseDialog}>
        <Box sx={{ pt: 2 }}>
          <Typography>{explanationText}</Typography>
        </Box>
      </ActionDialog>
    )
  return <></>
}

export default ExitUnsavedFormDialog
